import { useEffect, useContext, useState, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import Utils from "../../Utils";
import axios from "axios";
import _ from "lodash";

import CanvasArea from "../../components/photographer/CanvasArea";
import CommentModal from "../../components/photographer/CommentModal";
import ThumbnailList from "../../components/photographer/ThumbnailList";
import Swal from "sweetalert2";

import UserContext from "../../contexts/UserContext";

const PhViewFeedback = (props) => {
  const { psid, origid, fname } = useParams();
  const fileInputRef = useRef(null);
  const { userData } = useContext(UserContext);

  // active image index
  const [photoshoot, setPhotoshoot] = useState({});
  const [photoshootImages, setPhotoshootImages] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [activeImage, setActiveImage] = useState([]);
  const [aiFeedback, setAiFeedback] = useState([]);
  const [alreadyUploaded, setAlreadyUploaded] = useState([]);
  const [annotatedPhotoIds, setAnnotatedPhotoIds] = useState(new Set());

  useEffect(() => {
    axios
      .get(
        Utils.apiUrl(`photoshoot/photos?psid=${psid}&origid=${origid}`),
        Utils.apiHeaders(),
      )
      .then((res) => {
        if (res.data.success) {
          const annotatedIds = new Set(
            (res.data.annotated_photos || []).map((item) => Number(item.id)),
          );
          var pimages = res.data.data.filter(
            (img) => img.status == "bt-rejected",
          );
          var actindex = pimages.findIndex((item) => item.fname === fname);
          if (actindex == -1) actindex = 0;
          setAiFeedback(pimages[actindex].ai_feedback);
          setActiveIndex(actindex);
          setPhotoshootImages(pimages);
          setAlreadyUploaded(
            res.data.data.filter((img) => img.previous_photo_id == origid),
          );
          setAnnotatedPhotoIds(annotatedIds);
          setPhotoshoot(res.data.photoshoot);
          setActiveImage(pimages[actindex]);
        }
      })
      .catch((err) => {
        //callback(false, err.message);
      });
  }, []);

  const canUploadPhoto = () => {
    // Allow upload if BT rejected or if no feedback yet (first submission)
    return (
      ["bt-rejected"].includes(activeImage.status) &&
      ((Utils.isHotelOwner() &&
        _.get(photoshoot, "photographer_id", 0) <= 0 &&
        _.get(photoshoot, "hotel_id", 0) == Utils.getSelectedHotelId()) ||
        Utils.isPhotographer() ||
        (photoshoot.type != "GEN" &&
          (photoshoot.hotel_representative_id == 0 || Utils.isHR())))
    );
  };

  useEffect(() => {
    if (photoshootImages.length > 0 && activeIndex < photoshootImages.length) {
      const img = photoshootImages[activeIndex];
      setActiveImage(img);
      setAiFeedback(img.ai_feedback);
    }
  }, [activeIndex, photoshootImages]);

  // store per-image data: { [imageId]: { strokes: [...], comments: [...], image: src } }
  const [imageDataMap, setImageDataMap] = useState({});

  // comment modal state handled by CanvasArea -> forwarded here for global display if needed
  const [jsonOutput, setJsonOutput] = useState("");

  // Optional: backend endpoint placeholder (set to null to disable POST)
  let BACKEND_SAVE_URL = null; // e.g. "/api/visual-analyzer/save"

  // Called by CanvasArea when strokes/comments changed (autosave to local memory)
  const handleUpdateImageData = useCallback((imageId, data) => {
    setImageDataMap((prev) => ({ ...prev, [imageId]: data }));
  }, []);

  /* For Drag and Drop Images  */
  // Drag and drop handlers for Recce Images
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      fileInputRef.current.files = files;
      // Trigger change event to upload
      fileInputRef.current.dispatchEvent(
        new Event("change", { bubbles: true }),
      );
    }
  };

  const handleUploadNotNeeded = () => {
    Swal.fire({
      title: "Confirm",
      text: "Are you sure you want to mark this as 'Upload not needed'? This will notify the brand team that no revised image will be uploaded.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, confirm",
      cancelButtonText: "No, cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        console.log("Marking as Upload Not Needed for image ID:", activeImage);
        axios
          .put(
            Utils.apiUrl(
              `photoshoot/photo/upload-not-needed/${activeImage.id}`,
            ),
            { psid: activeImage.photoshoot_id },
            Utils.apiHeaders(),
          )
          .then((res) => {
            if (res.data.success) {
              Swal.fire({
                icon: "success",
                title: "Marked as Upload Not Needed",
                text: res.data.message,
                timer: 2000,
                showConfirmButton: false,
              });
              // Optionally, update local state to reflect this change
              setActiveImage((prev) => ({
                ...prev,
                status: "ai-processed",
              }));
            }
          })
          .catch((err) => {
            Swal.fire({
              icon: "error",
              title: "Action Failed",
              text: err.message,
              timer: 2000,
              showConfirmButton: false,
            });
          });
      }
    });
  };

  /* const handleFileChange = (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append('recces', files[i]);
      }
      axios
        .post(Utils.apiUrl(`photoshoot/recces/${id}`), formData, Utils.apiHeaders())
        .then((res) => {
          if(res.data.success) {
            updateRecceImages();
            alert(res.data.message);
          }
          else {
            alert(res.data.message);
          }
          // Optionally, show success message or update UI
        })
        .catch((err) => {
          console.error('Upload failed:', err);
          // Optionally, show error message
        });
    }
  }; */

  const handleClick = () => {
    fileInputRef.current.click();
  };

  const [rimgLoading, setRimgLoading] = useState(``);
  const submitUpdatedImage = (e) => {
    setRimgLoading(
      `<div className="text-blue-500 font-semibold py-4 loadingmess">Please wait. File upload in progress...</div>`,
    );
    const frm = e.currentTarget;
    e.preventDefault();

    const formData = new FormData();
    formData.append("psid", activeImage.photoshoot_id);
    formData.append(
      "origid",
      activeImage.orig_photo_id == null
        ? activeImage.id
        : activeImage.orig_photo_id,
    );
    formData.append("previd", activeImage.id);
    // Add attachments
    const files = frm.photo.files;
    for (let i = 0; i < files.length; i++) {
      formData.append("photo", files[i]);
    }

    axios
      .post(Utils.apiUrl(`photoshoot/photo`), formData, Utils.apiHeaders())
      .then((res) => {
        setRimgLoading(``);
        if (res.data.success) {
          Swal.fire({
            icon: "success",
            title: "Uploaded Revision",
            text: res.data.message,
            timer: 2000,
            showConfirmButton: false,
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "Upload Failed",
            text: res.data.message,
            timer: 2000,
            showConfirmButton: false,
          });
        }
        //console.log(frmMess);
      })
      .catch((err) => {
        Swal.fire({
          icon: "error",
          title: "Upload Failed",
          text: err.message,
          timer: 2000,
          showConfirmButton: false,
        });
        //callback(false, err.message);
      });
  };

  const submitReply = (e) => {
    const frm = e.currentTarget;
    e.preventDefault();

    const formData = new FormData();
    formData.append("photoId", activeImage.id);
    formData.append("remark", frm.remark.value);

    axios
      .put(
        Utils.apiUrl(`photoshoot/photo-remark`),
        formData,
        Utils.apiHeaders(),
      )
      .then((res) => {
        console.log(res.data);
        if (res.data.success) {
          Swal.fire({
            icon: "success",
            title: "Remark Submitted",
            text: res.data.message,
            timer: 2000,
            showConfirmButton: false,
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "Submission Failed",
            text: res.data.message,
            timer: 2000,
            showConfirmButton: false,
          });
        }
        //console.log(frmMess);
      })
      .catch((err) => {
        Swal.fire({
          icon: "error",
          title: "Submission Failed",
          text: err.message,
          timer: 2000,
          showConfirmButton: false,
        });
        //callback(false, err.message);
      });
  };

  const renderImageStatus = (img) => {
    if (!_.get(img, "id", false)) return "";
    const statuses = {
      "bt-rejected": ["Edits Needed", "bg-red-300"],
      "bt-approved": ["Approved", "bg-green-300"],
      "ai-processed": ["Pending", "bg-yellow-300"],
      "bt-edits-uploaded": ["Edits Uploaded", "bg-gray-300"],
    };
    const alreadyHasNewerVersion =
      _.get(
        photoshootImages.find((i) => i.orig_photo_id === img.id),
        "id",
        null,
      ) != null;

    let status =
      img.status == "bt-rejected" && alreadyHasNewerVersion
        ? "bt-edits-uploaded"
        : img.status;

    return (
      <span className={`rounded ml-2 p-1 px-3 ${statuses[status][1]} text-ms`}>
        {statuses[status][0]}
      </span>
    );
  };

  const hasAnnotatedImage = (img) => {
    return annotatedPhotoIds.has(Number(_.get(img, "id", 0)));
  };

  return (
    <>
      <div className="page-wrap">
        <main className="flex-area container mx-auto px-2">
          <div>
            <div className="text-center text-xl mb-2">
              <a
                href={`${process.env.REACT_APP_BASENAME}photoshoot-details/${psid}`}
                className="btn text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline w-full"
              >
                <i className="fas fa-arrow-left mr-2"></i> Back
              </a>
            </div>
            <aside
              className="thumb-column"
              style={{
                maxHeight: "calc(100vh - 100px)",
                overflowY: "auto",
                overflowX: "hidden",
              }}
              aria-label="Image thumbnails"
            >
              {photoshootImages.length > 0 && (
                <ThumbnailList
                  images={photoshootImages}
                  mode="photo"
                  activeId={activeImage.id}
                  origid={origid}
                  onSelect={(id) => {
                    const idx = photoshootImages.findIndex((i) => i.id === id);
                    if (idx >= 0) setActiveIndex(idx);
                  }}
                />
              )}
            </aside>
          </div>

          <section className=" flex-1">
            <div className="text-xl font-bold text-gray-800 w-full text-center flex-1 bg-gray-200 py-2 px-4 mb-2 rounded">
              Brand Feedback/Comments
              {renderImageStatus(activeImage)}
            </div>
            <div className="preview">
              <div className="p-2 bg-white border border-gray-200 rounded">
                <img
                  className="w-full h-auto"
                  src={`${process.env.REACT_APP_API_URL}/photoshoot/photo/full/${activeImage.fname}`}
                  alt={activeImage.filename || "Annotated image"}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 phfeedbackform">
                <div className="item">
                  <label className="text-base block mb-2">
                    Brand Team Feedback
                  </label>
                  <div className="feedback bg-gray-100 p-4 rounded-lg text-sm">
                    {activeImage.bt_feedback}
                  </div>
                </div>
                {canUploadPhoto() && (
                  <div className="item">
                    <form onSubmit={submitReply}>
                      <label className="text-base block mb-2">Your Reply</label>
                      <div className="block messagetobt mb-1">
                        {
                          <textarea
                            name="remark"
                            id="remark"
                            placeholder="Message to Brand Team"
                            className="border-0 rounded-lg w-full py-2 px-3 bg-white-100 border-gray-300 border focus:shadow-outline"
                            defaultValue={activeImage.photographer_remarks}
                          ></textarea>
                        }
                        {false && (
                          <div className="feedback bg-gray-100 p-4 rounded-lg text-sm">
                            {activeImage.photographer_remarks}
                          </div>
                        )}
                      </div>

                      {
                        <div className="cta text-right">
                          <button
                            type="submit"
                            className="btn text-white py-1 px-4 rounded focus:outline-none focus:shadow-outline"
                          >
                            Send
                          </button>
                        </div>
                      }
                    </form>
                  </div>
                )}
                {canUploadPhoto() && (
                  <div className="item">
                    {alreadyUploaded.length > 0 && (
                      <img
                        className="responsive p-4"
                        src={`${process.env.REACT_APP_API_URL}/photoshoot/photo/thumb/${alreadyUploaded[0].fname}`}
                        alt={alreadyUploaded[0].filename}
                      />
                    )}
                    {alreadyUploaded.length == 0 && (
                      <form onSubmit={submitUpdatedImage}>
                        <label className="text-base block mb-2">
                          Upload Revised Image
                        </label>
                        {rimgLoading && rimgLoading !== "" && (
                          <div
                            dangerouslySetInnerHTML={{ __html: rimgLoading }}
                          />
                        )}
                        <div className="feedback bg-gray-100 p-4 rounded-lg text-sm">
                          <input
                            name="photo"
                            type="file"
                            id="file-input"
                            accept="image/*"
                            className=""
                            ref={fileInputRef}
                          />
                          <div className="bg-yellow-100 text-yellow-800 p-2 rounded mt-2">
                            If you do not wish to upload a revised image, please
                            click "Upload not needed" to notify the brand team.
                          </div>
                        </div>
                        <div className="cta flex justify-between mt-2">
                          <button
                            type="button"
                            className="btn text-white py-1 px-2 rounded focus:outline-none focus:shadow-outline"
                            style={{ backgroundColor: "#138a44" }}
                            onClick={handleUploadNotNeeded}
                          >
                            Upload not needed
                          </button>
                          <button
                            type="submit"
                            className="btn text-white py-1 px-2 rounded focus:outline-none focus:shadow-outline"
                          >
                            Upload
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                )}
              </div>
            </div>
          </section>
        </main>
      </div>
    </>
  );
};

export default PhViewFeedback;
